import type { AppConfig } from '@/features/controller/configuration/models/appConfigSchema'
import { PowerMeterModel } from '@/features/controller/configuration/models/appConfigSchema'
import { LEGACY_SERIAL_CONNECTION_ID } from '@/features/controller/configuration/models/constants'
import type {
  AnyNamedModbusConnectionNode,
  AppConfigPowerMeterNode,
  DeviceConnectionNode,
  DeviceNode,
  ExternalModbusDeviceNode,
  LegacyModbusPowerMeterNode,
  LegacyModbusTcpConnectionNode,
  LegacySerialConnectionNode,
  NamedModbusSerialConnectionNode,
  NamedModbusTcpConnectionNode,
  TillquistPowerMeterNode,
} from '@/features/controller/configuration/models/controllerConfigTree'
import {
  DeviceConnectionNodeType,
  DeviceNodeType,
} from '@/features/controller/configuration/models/controllerConfigTree'
import type { ControllerConfigurationModel } from '@/features/controller/configuration/models/controllerConfiguration'
import { isTillquistEnabled } from '@/features/controller/configuration/models/raspiProperties/tillquist'
import type {
  ExternalModbusClientReference,
  LegacyModbusMeter,
  LegacyModbusTcpConnection,
  NamedModbusConnection,
  RaspiPropertiesConfig,
  TillquistConfig,
} from '@/features/controller/configuration/models/raspiProperties/types'
import { NamedModbusTransport } from '@/features/controller/configuration/models/raspiProperties/types'

function getAppConfigPowerMeterNodes(appConfig: AppConfig): AppConfigPowerMeterNode[] {
  const { powerMeters } = appConfig
  if ((powerMeters?.length ?? 0) > 0) {
    return powerMeters!.map((powerMeter, index) => ({
      config: powerMeter,
      connectionId: LEGACY_SERIAL_CONNECTION_ID,
      id: `${DeviceNodeType.AppConfigPowerMeter}-${index + 1}`,
      label: `${powerMeter.model} meter`,
      model: powerMeter.model,
      type: DeviceNodeType.AppConfigPowerMeter,
    }))
  } else {
    return []
  }
}

function getTillquistPowerMeterNodeFromRaspiProperties(
  tillquistConfig: TillquistConfig,
): TillquistPowerMeterNode | undefined {
  if (isTillquistEnabled(tillquistConfig)) {
    return {
      config: tillquistConfig,
      connectionId: LEGACY_SERIAL_CONNECTION_ID,
      id: 'tillquist-1',
      label: 'Tillquist',
      model: 'Tillquist',
      type: DeviceNodeType.Tillquist,
    }
  }
  return undefined
}

function getLegacySerialModbusMeterNodesFromRaspiProperties(
  raspiPropertiesConfig: RaspiPropertiesConfig,
): LegacyModbusPowerMeterNode[] {
  return raspiPropertiesConfig.legacyModbusConnections
    .filter((conn) => conn['serialPort'] !== undefined)
    .flatMap((conn) =>
      conn.meters.map((meter) => ({
        config: meter,
        connectionId: LEGACY_SERIAL_CONNECTION_ID,
        id: 'modbus-' + meter.number,
        errors: meter.errors,
        label: `Modbus meter #${meter.number}`,
        model: 'Modbus',
        type: DeviceNodeType.LegacyModbus,
      })),
    )
}

export function makeLegacyModbusTcpConnectionNode(conn: LegacyModbusTcpConnection): LegacyModbusTcpConnectionNode {
  return {
    config: conn,
    id: conn.id,
    errors: conn.errors,
    label: 'Legacy Modbus/TCP',
    type: DeviceConnectionNodeType.LegacyModbusTcp,
  }
}

export function makeNamedModbusTcpConnectionNode(
  conn: NamedModbusTcpConnectionNode['config'],
): NamedModbusTcpConnectionNode {
  return {
    config: conn,
    id: conn.id,
    errors: conn.errors,
    label: conn.namedClient.name,
    type: DeviceConnectionNodeType.NamedModbusTcp,
  }
}

export function makeNamedModbusSerialConnectionNode(
  conn: NamedModbusSerialConnectionNode['config'],
): NamedModbusSerialConnectionNode {
  return {
    config: conn,
    id: conn.id,
    errors: conn.errors,
    label: conn.namedClient.name,
    type: DeviceConnectionNodeType.NamedModbusSerial,
  }
}

function makeNamedModbusConnectionNode(conn: NamedModbusConnection): AnyNamedModbusConnectionNode {
  return conn.namedClient.transport === NamedModbusTransport.tcp
    ? makeNamedModbusTcpConnectionNode(conn as NamedModbusTcpConnectionNode['config'])
    : makeNamedModbusSerialConnectionNode(conn as NamedModbusSerialConnectionNode['config'])
}

export function getDeviceConnectionNodesFromRaspiProperties(
  raspiPropertiesConfig: RaspiPropertiesConfig,
): DeviceConnectionNode[] {
  const { legacyModbusSerialConfig, legacySerialPortConfig, legacyModbusConnections } = raspiPropertiesConfig
  const defaultSerialPortConfig: LegacySerialConnectionNode = {
    config: {
      legacySerialPortConfig,
      legacyModbusSerialConfig,
    },
    id: LEGACY_SERIAL_CONNECTION_ID,
    errors: legacyModbusConnections.filter((conn) => conn['serialPort'] !== undefined).flatMap((conn) => conn.errors),
    label: 'Serial port',
    type: DeviceConnectionNodeType.LegacySerial,
  }
  const modbusTcpConnections: LegacyModbusTcpConnectionNode[] = legacyModbusConnections
    .filter((conn) => conn['tcpTarget'] !== undefined)
    .map((conn) => makeLegacyModbusTcpConnectionNode(conn as LegacyModbusTcpConnection))
  const namedModbusConnections: AnyNamedModbusConnectionNode[] = legacyModbusConnections
    .filter((conn) => conn['namedClient'] !== undefined)
    .map((conn) => makeNamedModbusConnectionNode(conn as NamedModbusConnection))

  return [defaultSerialPortConfig, ...modbusTcpConnections, ...namedModbusConnections]
}

function getDevicesForLegacySerialConnection(
  connectionNode: LegacySerialConnectionNode,
  configuration: ControllerConfigurationModel,
): DeviceNode[] {
  const tillquistNode = configuration.raspiPropertiesConfig
    ? getTillquistPowerMeterNodeFromRaspiProperties(configuration.raspiPropertiesConfig.tillquistConfig)
    : undefined
  const modbusSerialNodes = configuration.raspiPropertiesConfig
    ? getLegacySerialModbusMeterNodesFromRaspiProperties(configuration.raspiPropertiesConfig)
    : []
  const appConfigNodes = configuration.appConfig
    ? getAppConfigPowerMeterNodes(configuration.appConfig).map((meter) => {
        if (tillquistNode && meter.model === PowerMeterModel.Generic) {
          return {
            ...meter,
            label: 'Additional Tillquist settings',
            isTillquistSettingsNode: true,
          }
        } else {
          return meter
        }
      })
    : []
  return [...(tillquistNode ? [tillquistNode] : []), ...appConfigNodes, ...modbusSerialNodes]
}

export function makeLegacyModbusMeterNode(
  connectionNode: DeviceConnectionNode,
  meter: LegacyModbusMeter,
): LegacyModbusPowerMeterNode {
  return {
    type: DeviceNodeType.LegacyModbus,
    config: meter,
    connectionId: connectionNode.id,
    id: `modbus-${meter.number}`,
    errors: meter.errors,
    label: `Modbus meter #${meter.number}`,
    model: 'Modbus',
  }
}

function getModbusMeterNodesForConnection(
  connectionNode: LegacyModbusTcpConnectionNode | AnyNamedModbusConnectionNode,
): DeviceNode[] {
  return connectionNode.config.meters.map((meter) => makeLegacyModbusMeterNode(connectionNode, meter))
}

function makeExternalModbusDeviceNode(
  connectionNode: DeviceConnectionNode,
  reference: ExternalModbusClientReference,
): ExternalModbusDeviceNode {
  return {
    config: reference,
    connectionId: connectionNode.id,
    id: `external-${reference.propertyKey}`,
    // The displayed name needs translation, so it is composed at render time. Never the raw family,
    // which is a property key segment.
    label: reference.deviceId ?? '',
    model: reference.family,
    type: DeviceNodeType.ExternalModbusDevice,
  }
}

/**
 * Everything else on this connection. Read-only, since these configurations have no drawer here, but
 * shown because "what else talks to this endpoint" is the question an operator actually has.
 */
function getExternalDevicesForNamedConnection(
  connectionNode: AnyNamedModbusConnectionNode,
  configuration: ControllerConfigurationModel,
): ExternalModbusDeviceNode[] {
  const references = configuration.raspiPropertiesConfig?.externalModbusClientReferences ?? []
  return references
    .filter((reference) => reference.clientName === connectionNode.config.namedClient.name)
    .map((reference) => makeExternalModbusDeviceNode(connectionNode, reference))
}

export function getDevicesForConnectionNode(
  connectionNode: DeviceConnectionNode,
  configuration: ControllerConfigurationModel,
): DeviceNode[] {
  switch (connectionNode.type) {
    case DeviceConnectionNodeType.LegacySerial:
      return getDevicesForLegacySerialConnection(connectionNode, configuration)
    case DeviceConnectionNodeType.LegacyModbusTcp:
      return getModbusMeterNodesForConnection(connectionNode)
    case DeviceConnectionNodeType.NamedModbusTcp:
    case DeviceConnectionNodeType.NamedModbusSerial:
      return [
        ...getModbusMeterNodesForConnection(connectionNode),
        ...getExternalDevicesForNamedConnection(connectionNode, configuration),
      ]
  }
}
